<template>
  <InfiniteScroll class="character-list" :status="loadStatus" :empty="false" @loadMore="next">
    <div class="x-container pt-5">
      <BannerSearch
        :title="pageTitle"
        :cover="pageBackground"
        :show-recommend="true"
        :placeholder="$t('search-for-3d-avatars')"
        :showSearch="true"
        :keyword="keyword"
        :area="SEARCH_RECORD_UNIQUE.CHARACTER"
        @search="handleOnSearch"
      />

      <!-- 标签过滤器 -->
      <div ref="filterBoxRef" class="library-avatar__filter">
        <div class="library-avatar__controls">
          <OptionSwitcher
            v-model:model-value="searchForm.sort"
            :options="sortOptions"
            @update:model-value="handleFilterChange"
          />
          <div class="library-avatar__controls-right">
            <!-- <SelectRight
              :model-value="searchForm.config_types"
              :right-type="userInfo?.rights_type"
              @update:modelValue="handleRightChange"
            /> -->
            <template v-for="(tag, index) in characterTag?.tags" :key="tag.tag_id">
              <div v-if="index !== 0" class="library-avatar__divider"></div>
              <Select
                v-model="searchForm.tags[tag.tag_id]"
                :placeholder="tag.tag_name"
                :options="processedTagOptions(tag)"
                @change="handleFilterChange"
                @clear="handleFilterChange"
              />
            </template>
          </div>
        </div>
      </div>
      <a-spin :spinning="loading && calList.length > 0">
        <div class="grid grid-cols-5 gap-x-[12px] gap-y-[30px]">
          <ContentCharacterCard
            v-for="digitalHuman in calList"
            :key="digitalHuman.id"
            :data="digitalHuman"
            target="_blank"
            @change="onChange($event, digitalHuman)"
          >
          </ContentCharacterCard>
        </div>

        <Empty
          v-if="searchStatus === 'success' && !list.length"
          :list="popularList || []"
          @refresh="popularRefresh"
          @select="handleOnSearch"
        />
      </a-spin>
    </div>
  </InfiniteScroll>
  <BackTop scrollContainer=".character-list" item=".character-card" :total="total" />
</template>
<script lang="ts" setup>
import BackTop from '@rendora/common/features/back-top/index.vue';
import { usePayment } from '@rendora/common/features/payment/store';
import { OptionSwitcher } from '@rendora/design/components/option-switcher';
import { Select } from '@rendora/design/components/select';
import { useI18n } from 'vue-i18n';
import { getPromotionCenterPageCard } from '~/api';
import type { Tag } from '~/api/interface';
import type { Character } from '~/api/interface/character';
import { PAGE_CARD_TYPE } from '~/api/interface/publicize';
import { getCharacterList } from '~/api/module/character';
import { EditIndexType, getRandomNames, MaterialType } from '~/api/module/material';
import BannerSearch from '~/components/banner/search.vue';
import { SEARCH_RECORD_UNIQUE, TEMPLATE_SORT } from '~/constants';
import { PageKey } from '~/constants/page';
import Empty from '~/features/searchInput/empty.vue';
import { SCROLL_LIST, useScrollList } from '~/hooks/useScrollList';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useStarPatchList } from '~/hooks/useStarPatch';
import { isChinese } from '~/utils/area';

const { t: $t } = useI18n();

provideResourceLocation({
  source: SOURCE.CHARACTER,
});

const route = useRoute();
const userInfo = useUserInfo();
const paymentStore = usePayment();

const keyword = ref<string>((route.query.keyword as unknown as string) || '');
const characterTag = useCharacterTags();

const genderTag = computed(() => characterTag.value.tags.find(tag => tag.outer_id === 'gender'));
const maleTag = computed(() => genderTag.value?.children?.find(child => child.outer_id === 'Male'));

const processedTagOptions = (item: Tag) => {
  return item.children
    ?.filter(tag => {
      // 假如当前选中的是 Male，则不显示 Elegant_Style 和 ABG_Style 这两个标签
      if (
        genderTag.value?.tag_id &&
        searchForm.tags[genderTag.value?.tag_id] === maleTag.value?.tag_id &&
        tag.outer_id
      ) {
        return !['Elegant_Style', 'ABG_Style'].includes(tag.outer_id);
      }
      return true;
    })
    ?.map(tag => ({
      value: tag.tag_id,
      label: tag.tag_name,
    }));
};

const searchForm = reactive({
  sort: TEMPLATE_SORT.ALL,
  tags: {} as Record<string, number>,
  // config_types: '',
  config_types: '',
});

// 过滤条件变化处理
const handleFilterChange = () => {
  refresh();
};

// const handleRightChange = async (config_types?: string) => {
//   searchForm.config_types = config_types || '';
//   loading.value = true;
//   await refresh();
//   loading.value = false;
// };
const handleRightChange = async (config_types?: string) => {
  searchForm.config_types = config_types || '';
  loading.value = true;
  await refresh();
  loading.value = false;
};

const industryTag = computed(() =>
  characterTag.value.tags.find(tag => tag.outer_id === INDUSTRY_OUTER_ID),
);

const sceneTag = computed(() =>
  characterTag.value.tags.find(tag => tag.outer_id === SCENE_OUTER_ID),
);

const SCENE_OUTER_ID = 'character_application';

const INDUSTRY_OUTER_ID = 'character_industry';

const industryReg = /hy(\d+)/;
const sceneReg = /cj(\d+)/;

const industryScene = computed(() => {
  const params = route.params.industryScene as string;
  if (params) {
    const industryStr = params.match(industryReg)?.[1];
    const sceneStr = params.match(sceneReg)?.[1];
    let industry: Tag | undefined;
    let scene: Tag | undefined;

    if (industryStr) {
      industry = industryTag.value?.children?.find(child => child.tag_id === Number(industryStr));
    }

    if (sceneStr) {
      scene = sceneTag.value?.children?.find(child => child.tag_id === Number(sceneStr));
    }

    return [industry, scene];
  }
  return undefined;
});

watch(
  () => industryScene.value,
  val => {
    if (val && !val[0] && !val[1]) {
      navigateTo({ name: PageKey.NOT_FOUND });
    }
  },
  { immediate: true, flush: 'sync' },
);

const { data: popularList, refresh: popularRefresh } = useAsyncData('popularList', async () => {
  const res = await getRandomNames({
    group_id: MaterialType.Character,
    size: 6,
    edit_index_type: EditIndexType.Change,
  });
  return res.data;
});

watchEffect(
  () => {
    const tagIds = (route.query.tag_ids || '') as string;
    const tagIdArr = tagIds.split(',');

    tagIdArr.forEach(tagId => {
      const [parentId, childId] = tagId.split('.');
      if (parentId && childId) {
        searchForm.tags[parentId] = Number(childId);
      }
    });

    if (industryScene.value?.[0]) {
      searchForm.tags[industryScene.value[0].pid!] = industryScene.value[0].tag_id;
    }

    if (industryScene.value?.[1]) {
      searchForm.tags[industryScene.value[1].pid!] = industryScene.value[1].tag_id;
    }
  },
  { flush: 'sync' }, // 避免后面接口获取的时候，tag_ids还没更新
);

const sortOptions = useDict().sorts.map(item => ({
  value: item.key,
  label: item.label,
  key: item.key,
}));

const getTagIds = (excludeIndustryAndScene: boolean) => {
  const tagIds: string[] = [];

  Object.keys(searchForm.tags).forEach(key => {
    if (excludeIndustryAndScene) {
      if ([industryTag.value?.tag_id, sceneTag.value?.tag_id].includes(Number(key))) {
        return;
      }
    }

    if (searchForm.tags[key]) {
      tagIds.push(`${key}.${searchForm.tags[key]}`);
    }
  });

  return tagIds.join(',');
};

const router = useRouter();

function changePath() {
  const arr: string[] = [];

  let path = '/characters';

  if (industryTag.value) {
    const childId = searchForm.tags[industryTag.value.tag_id];
    const childTag = industryTag.value.children?.find(child => child.tag_id === childId);

    if (childTag) {
      arr.push(`hy${childTag.tag_id}`);
    }
  }

  if (sceneTag.value) {
    const childId = searchForm.tags[sceneTag.value.tag_id];
    const childTag = sceneTag.value.children?.find(child => child.tag_id === childId);

    if (childTag) {
      arr.push(`cj${childTag.tag_id}`);
    }
  }

  if (arr.length) {
    path += `/${arr.join('')}`;
  }

  const query = {
    keyword: keyword.value || undefined,
    tag_ids: getTagIds(true) || undefined,
  };

  router.replace({
    path,
    query,
  });
}

const {
  list,
  refresh,
  pending,
  status: searchStatus,
  next,
  total,
  abort,
} = useScrollList(
  async (params, abortControl) => {
    const tag_ids = getTagIds(false);

    const res = await getCharacterList(
      {
        group_id: 1,
        page: params.page,
        page_size: params.page_size,
        many: 1,
        intersection_tag_ids: tag_ids,
        o: `-${searchForm.sort}`,
        query_star: import.meta.client,
        // config_types: searchForm.config_types,
        keyword: keyword.value,
      },
      abortControl,
    );

    deepOmit(res, ['look']);

    res.data?.results?.forEach(item => {
      item.query_star = import.meta.client; // 标记这些数据是否查询过star
    });

    if (import.meta.client) {
      changePath();
    }

    return res;
  },
  {
    key: SCROLL_LIST.CHARACTER,
    pageSize: 50,
  },
);

onBeforeUnmount(() => {
  abort();
});

const { calList, status } = useStarPatchList(list, 3);

const { data: pageData } = useAsyncData('promotionCenterPageCard', async () => {
  const res = await getPromotionCenterPageCard({
    type: PAGE_CARD_TYPE.CHARACTER,
  });
  return res.data;
});

const pageTitle = computed(() => pageData.value?.[0]?.title || '');
const pageBackground = computed(() => pageData.value?.[0]?.cover_url || '');

const loadStatus = computed(() => {
  if (searchStatus.value === 'success' && total.value === 0) {
    return 'noData';
  }

  if (searchStatus.value === 'success' && list.value.length >= total.value) {
    return 'noMore';
  }

  if (pending.value) {
    return 'loading';
  }

  if (status.value === 'waiting') {
    return 'loading';
  }
  if (status.value === 'error') {
    return 'loadComplete';
  }
  if (status.value === 'success') {
    return 'loadComplete';
  }

  return 'loadComplete';
});

const loading = ref(false);
const handleOnSearch = async (val: string) => {
  keyword.value = val;
  loading.value = true;
  await refresh();
  loading.value = false;
};

useBackToTop(() => ({
  scrollContainer: '.infiniteScroll',
  item: '.character-card',
  total: total.value,
}));

const onChange = ([key, value]: [string, Character], data: Character) => {
  const item: any = calList.value?.find(item => item.id === data.id);
  if (item) {
    item[key] = value;
  }
};

const seoData = computed(() => {
  let seo_title = '';
  let seo_description = '';
  let seo_keywords = '';
  if (industryScene.value?.[1]?.outer_id === 'character_education_and_teaching' && isChinese()) {
    seo_title = $t('character-list-scenarios-edu-seo-title', [industryScene.value[1].tag_name]);
    seo_description = $t('character-list-scenarios-edu-seo-description', [
      industryScene.value[1].tag_name,
    ]);
    seo_keywords = $t('character-list-scenarios-edu-seo-keywords', [
      industryScene.value[1].tag_name,
    ]);
    if (industryScene.value?.[0]) {
      seo_title = $t('character-list-industry-scenarios-edu-seo-title', [
        industryScene.value[0].tag_name,
        industryScene.value[1].tag_name,
      ]);
      seo_description = $t('character-list-industry-scenarios-edu-seo-description', [
        industryScene.value[0].tag_name,
        industryScene.value[1].tag_name,
      ]);
    }
  } else if (industryScene.value?.[1] && industryScene.value[0]) {
    seo_title = $t('character-list-industry-scenarios-seo-title', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ]);
    seo_description = $t('character-list-industry-scenarios-seo-description', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ]);
    seo_keywords = $t('character-list-scenarios-seo-keywords', [
      industryScene.value[0].tag_name,
      industryScene.value[1].tag_name,
    ]);
  } else if (industryScene.value?.[1] && !industryScene.value[0]) {
    seo_title = $t('character-list-scenarios-seo-title', [industryScene.value[1].tag_name]);
    seo_description = $t('character-list-scenarios-seo-description', [
      industryScene.value[1].tag_name,
    ]);
    seo_keywords = $t('character-list-scenarios-seo-keywords', [industryScene.value[1].tag_name]);
  } else if (!industryScene.value?.[1] && industryScene.value?.[0]) {
    seo_title = $t('character-list-industry-seo-title', [industryScene.value[0].tag_name]);
    seo_description = $t('character-list-industry-seo-description', [
      industryScene.value[0].tag_name,
    ]);
    seo_keywords = $t('character-list-industry-seo-keywords', [industryScene.value[0].tag_name]);
  } else {
    seo_title = $t('character-list-seo-title', []);
    seo_description = $t('character-list-seo-description', []);
    seo_keywords = $t('character-list-seo-keywords', []);
  }

  return {
    seo_title: seo_title.trim(),
    seo_description: seo_description.trim(),
    seo_keywords: seo_keywords.trim(),
  };
});

useWatchPageSeoData(seoData);

onMounted(async () => {
  await paymentStore.fetchDiscount();
});
</script>
<style lang="scss" scoped>
.character-list {
  height: calc(100vh - 60px);
  overflow-y: scroll;
  position: relative;
}

.top-area {
  padding-top: 90px;
  padding-bottom: 70px;
}
.library-avatar__filter {
  position: sticky;
  top: 0px;
  z-index: 11;
  background: var(--x-back-1);
}

.library-avatar__controls {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 20px 0;
}

.library-avatar__controls-right {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
}

.library-avatar__divider {
  width: 1px;
  height: 12px;
  margin: 0 9px;
  background: var(--x-back-5);
}
</style>
